import { Link } from "react-router-dom";
import {
  SITE_PAGE_SHELL_CLASS,
  SitePageAtmosphere,
  SITE_PAGE_BODY_PADDING_TOP,
} from "./SitePageChrome.jsx";

/* Closed-state sibling of the 404's HUD frame: same chassis, corner brackets,
 * and telemetry rails, but the readouts report a full house rather than a
 * failure, so the accent is gold instead of the 404's alarm pink. */

const GOLD = "#c9973a";

const CornerBracket = ({ position }) => {
  const edges = {
    "top-left": "-top-1 -left-1 border-t-4 border-l-4",
    "top-right": "-top-1 -right-1 border-t-4 border-r-4",
    "bottom-left": "-bottom-1 -left-1 border-b-4 border-l-4",
    "bottom-right": "-bottom-1 -right-1 border-b-4 border-r-4",
  };
  return (
    <span
      aria-hidden
      className={`absolute w-7 h-7 border-[#c9973a] ${edges[position]}`}
    />
  );
};

/* The 404 draws a span that runs out of deck mid-air. This one is finished:
 * both towers up, the cable landed, the deck continuous shore to shore. */
const FinishedBridge = () => (
  <svg
    viewBox="0 0 900 210"
    className="w-full max-w-3xl mx-auto mt-12 sm:mt-16"
    aria-hidden
    role="presentation"
  >
    <g stroke={GOLD} fill="none">
      {/* deck */}
      <line x1="0" y1="172" x2="900" y2="172" strokeWidth="6" />
      {/* towers */}
      <line x1="298" y1="30" x2="298" y2="192" strokeWidth="9" />
      <line x1="278" y1="60" x2="318" y2="60" strokeWidth="4" />
      <line x1="602" y1="30" x2="602" y2="192" strokeWidth="9" />
      <line x1="582" y1="60" x2="622" y2="60" strokeWidth="4" />
      {/* main cable: shore, over both towers, shore */}
      <path d="M0 96 C 110 158, 210 62, 296 38" strokeWidth="3.5" />
      <path d="M300 38 C 360 140, 540 140, 600 38" strokeWidth="3.5" />
      <path d="M604 38 C 690 62, 790 158, 900 96" strokeWidth="3.5" />
      {/* hangers, left approach */}
      <line x1="45" y1="118" x2="45" y2="172" strokeWidth="2" />
      <line x1="95" y1="132" x2="95" y2="172" strokeWidth="2" />
      <line x1="145" y1="126" x2="145" y2="172" strokeWidth="2" />
      <line x1="195" y1="99" x2="195" y2="172" strokeWidth="2" />
      <line x1="245" y1="66" x2="245" y2="172" strokeWidth="2" />
      {/* hangers, main span */}
      <line x1="350" y1="88" x2="350" y2="172" strokeWidth="2" />
      <line x1="400" y1="108" x2="400" y2="172" strokeWidth="2" />
      <line x1="450" y1="115" x2="450" y2="172" strokeWidth="2" />
      <line x1="500" y1="108" x2="500" y2="172" strokeWidth="2" />
      <line x1="550" y1="88" x2="550" y2="172" strokeWidth="2" />
      {/* hangers, right approach */}
      <line x1="655" y1="66" x2="655" y2="172" strokeWidth="2" />
      <line x1="705" y1="99" x2="705" y2="172" strokeWidth="2" />
      <line x1="755" y1="126" x2="755" y2="172" strokeWidth="2" />
      <line x1="805" y1="132" x2="805" y2="172" strokeWidth="2" />
      <line x1="855" y1="118" x2="855" y2="172" strokeWidth="2" />
    </g>
  </svg>
);

const ApplyClosedPage = () => {
  return (
    <div
      className={`${SITE_PAGE_SHELL_CLASS} flex flex-col justify-center items-center px-4 sm:px-6 pb-16 ${SITE_PAGE_BODY_PADDING_TOP}`}
    >
      <SitePageAtmosphere />

      <div className="relative z-10 w-full max-w-5xl">
        <div className="relative border border-[#7fd4ee]/40 bg-[#101538]/60">
          <CornerBracket position="top-left" />
          <CornerBracket position="top-right" />
          <CornerBracket position="bottom-left" />
          <CornerBracket position="bottom-right" />

          {/* status rail */}
          <div className="flex justify-between items-center px-5 sm:px-8 pt-5 font-mono text-xs sm:text-sm tracking-[0.25em]">
            <span className="flex items-center gap-2.5 text-[#c9973a]">
              <span className="ac-pulse-dot w-2 h-2 rounded-full bg-[#c9973a]" aria-hidden />
              GATES CLOSED
            </span>
            <span className="text-[#97f2f8]">REG.END</span>
          </div>

          <div className="px-5 sm:px-8 py-10 sm:py-14 text-center">
            <h1 className="font-mono font-bold text-5xl sm:text-7xl md:text-8xl leading-none text-[#97f2f8] tracking-[0.08em]">
              CLOSED
            </h1>

            <h2 className="mt-6 text-2xl sm:text-3xl font-bold text-white tracking-tight">
              Registration for SteelHacks XIII has closed
            </h2>

            <p className="mt-4 max-w-xl mx-auto text-gray-300 text-sm sm:text-base leading-relaxed">
              At this time we are no longer taking new applications. If you are
              a Pitt or CMU student still trying to register, reach out to us
              at{" "}
              <a
                href="mailto:pittcsc@gmail.com"
                className="text-cyan-400 hover:text-cyan-300 font-semibold underline transition-colors"
              >
                pittcsc@gmail.com
              </a>{" "}
              and we will do our best to help. Thanks!
            </p>
          </div>

          {/* telemetry rail */}
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-between sm:items-center px-5 sm:px-8 pb-5 font-mono text-xs sm:text-sm tracking-[0.2em] text-[#97f2f8]/80">
            <span className="whitespace-nowrap">LAT 40.4444 LON -79.9608</span>
            <span className="whitespace-nowrap">CAPACITY REACHED 100%</span>
          </div>
        </div>

        {/* CTAs - one clear primary */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-10">
          <Link
            to="/login"
            className="w-full sm:w-auto text-center px-8 py-3 bg-gradient-to-r from-cyan-400 to-pink-400 text-gray-900 font-bold rounded-full shadow-lg hover:shadow-cyan-400/20 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
          >
            Already applied? Log in
          </Link>
          <Link
            to="/"
            className="hidden sm:block sm:w-auto text-center px-8 py-3 border border-white/25 text-white font-bold rounded-full hover:border-cyan-400 hover:bg-cyan-400/10 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
          >
            Back to Home
          </Link>
        </div>

        <FinishedBridge />
      </div>

      <style>{`
        @keyframes ac-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.25; }
        }
        .ac-pulse-dot {
          animation: ac-pulse 2.4s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .ac-pulse-dot { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default ApplyClosedPage;
